import React, { useState, useMemo } from 'react';

/**
 * ScheduleSendModal - Schedule email for later delivery with smart time suggestions
 */

const ScheduleSendModal = ({ isOpen, onClose, onSchedule, recipientTimezone }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Calculate smart schedule options
  const scheduleOptions = useMemo(() => {
    const now = new Date();
    const options = [];

    // Later today (3 hours from now, rounded to next hour)
    const laterToday = new Date(now);
    laterToday.setHours(laterToday.getHours() + 3);
    laterToday.setMinutes(0);
    laterToday.setSeconds(0);

    // Only show "Later today" if it's before 6 PM
    if (laterToday.getHours() < 18) {
      options.push({
        id: 'later-today',
        label: 'Later today',
        time: formatTime(laterToday),
        date: laterToday,
        icon: '☀️',
      });
    }

    // Tomorrow morning at 9 AM
    const tomorrowMorning = new Date(now);
    tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
    tomorrowMorning.setHours(9, 0, 0, 0);
    options.push({
      id: 'tomorrow-morning',
      label: 'Tomorrow morning',
      time: formatTime(tomorrowMorning),
      date: tomorrowMorning,
      icon: '🌅',
    });

    // Tomorrow afternoon at 2 PM
    const tomorrowAfternoon = new Date(now);
    tomorrowAfternoon.setDate(tomorrowAfternoon.getDate() + 1);
    tomorrowAfternoon.setHours(14, 0, 0, 0);
    options.push({
      id: 'tomorrow-afternoon',
      label: 'Tomorrow afternoon',
      time: formatTime(tomorrowAfternoon),
      date: tomorrowAfternoon,
      icon: '🌤️',
    });

    // Next Monday at 9 AM (if today is not Monday)
    const nextMonday = new Date(now);
    const daysUntilMonday = (8 - nextMonday.getDay()) % 7 || 7;
    nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
    nextMonday.setHours(9, 0, 0, 0);
    if (daysUntilMonday > 1) {
      options.push({
        id: 'next-monday',
        label: 'Monday morning',
        time: formatTime(nextMonday),
        date: nextMonday,
        icon: '📅',
      });
    }

    // Weekend (Saturday 10 AM) - only if it's weekday
    const dayOfWeek = now.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const saturday = new Date(now);
      saturday.setDate(saturday.getDate() + (6 - dayOfWeek));
      saturday.setHours(10, 0, 0, 0);
      options.push({
        id: 'weekend',
        label: 'This weekend',
        time: formatTime(saturday),
        date: saturday,
        icon: '🌴',
      });
    }

    return options;
  }, []);

  function formatTime(date) {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  }

  function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const handleOptionSelect = (option) => {
    setSelectedOption(option.id);
    setShowCustomPicker(false);
  };

  const handleCustomClick = () => {
    setShowCustomPicker(true);
    setSelectedOption('custom');
    // Set default custom date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setCustomDate(formatDateForInput(tomorrow));
  };

  const handleSchedule = () => {
    let scheduledDate;

    if (selectedOption === 'custom') {
      if (!customDate) return;
      const [year, month, day] = customDate.split('-').map(Number);
      const [hours, minutes] = customTime.split(':').map(Number);
      scheduledDate = new Date(year, month - 1, day, hours, minutes);
    } else {
      const option = scheduleOptions.find((o) => o.id === selectedOption);
      if (!option) return;
      scheduledDate = option.date;
    }

    // Validate the date is in the future
    if (scheduledDate <= new Date()) {
      alert('Please select a time in the future');
      return;
    }

    onSchedule(scheduledDate);
    onClose();
  };

  // Get minimum date (today)
  const minDate = formatDateForInput(new Date());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#111] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-[#222]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-brand"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Schedule Send
              </h2>
              <p className="text-xs text-gray-500 dark:text-[#666]">
                Choose when to send this email
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-[#222] transition-colors"
          >
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Quick options */}
          <div className="space-y-2 mb-4">
            {scheduleOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  selectedOption === option.id
                    ? 'bg-brand/10 border-2 border-brand'
                    : 'bg-gray-50 dark:bg-[#1A1A1A] border-2 border-transparent hover:border-gray-200 dark:hover:border-[#333]'
                }`}
              >
                <span className="text-xl">{option.icon}</span>
                <div className="flex-1 text-left">
                  <p
                    className={`font-medium ${
                      selectedOption === option.id
                        ? 'text-brand'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {option.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#666]">
                    {option.time}
                  </p>
                </div>
                {selectedOption === option.id && (
                  <svg
                    className="w-5 h-5 text-brand"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* Custom option */}
          <button
            onClick={handleCustomClick}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all mb-4 ${
              selectedOption === 'custom'
                ? 'bg-brand/10 border-2 border-brand'
                : 'bg-gray-50 dark:bg-[#1A1A1A] border-2 border-dashed border-gray-300 dark:border-[#333] hover:border-gray-400 dark:hover:border-[#444]'
            }`}
          >
            <span className="text-xl">📆</span>
            <p
              className={`font-medium ${
                selectedOption === 'custom'
                  ? 'text-brand'
                  : 'text-gray-600 dark:text-[#888]'
              }`}
            >
              Pick a date & time
            </p>
          </button>

          {/* Custom date/time picker */}
          {showCustomPicker && (
            <div className="p-4 bg-gray-50 dark:bg-[#1A1A1A] rounded-xl mb-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-[#666] mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={customDate}
                  min={minDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-[#666] mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-brand"
                />
              </div>
            </div>
          )}

          {/* Recipient timezone notice */}
          {recipientTimezone && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-600 dark:text-blue-400 mb-4">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Recipient is in {recipientTimezone} timezone</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-[#222]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-[#888] hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={!selectedOption}
            className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 ${
              selectedOption
                ? 'bg-brand text-white hover:bg-brand-hover'
                : 'bg-gray-200 dark:bg-[#333] text-gray-400 dark:text-[#666] cursor-not-allowed'
            }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSendModal;
